import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
    Box, Button, IconButton, InputAdornment, TextField, Tooltip, Typography, useTheme
} from '@mui/material'
import { toast } from 'react-toastify'
import { observer } from 'mobx-react'

import CustomTable from '../../../../components/common/custom-table/CustomTable'
import CustomSelectField from '../../../../components/common/CustomSelectField'
import CustomChip from '../../../../components/common/CustomChip'
import AddRecordingDialog from '../../../components/admin/hybrid-class-videos/AddRecordingDialog.jsx'
import RecordingPreviewDialog, { RECORDING_STATUS_COLORS } from '../../../components/admin/hybrid-class-videos/RecordingPreviewDialog.jsx'
import { useHybridClassVideoUploadStore } from '../../../../store/hybridclassvideoupload.store'
import { Icons, formatDate, formatVideoDuration, getFileSizeFromBytes, getTimePassedFrom } from '../../../../utils/utilities'
import api from '../../../../service/api.js'
import Message from '../../../../shared/components/common/Message.jsx'
import DataLoadingSpinner from '../../../../components/common/DataLoadingSpinner.js'

const loadingSkeleton = <DataLoadingSpinner waitingMessage="Loading recordings, please wait..." />

const MODULES = [{ label: "Module 1", value: 1 }, { label: "Module 2", value: 2 }, { label: "Module 3", value: 3 }, { label: "Module 4", value: 4 }, { label: "Module 5", value: 5 }]
const RECORDING_STATUSES = ["uploaded", "uploading", "failed"]
const STATUS_COLOR_MAP = RECORDING_STATUS_COLORS

const menuTextStyles = { textTransform: "capitalize" }
const moduleFilterOptions = [{ label: "All modules", value: "all" }, ...MODULES.map( mod => ( mod ) )]
const statusFilterOptions = [{ label: "All statuses", value: "all" }, ...RECORDING_STATUSES.map( status => ( { label: status, value: status } ) )]

const exportRecordingsToCsv = ( rows ) => {
    const header = ["Title", "File", "Subject", "Module", "Topics", "Duration", "Uploaded At", "Status"]
    const escapeCell = ( cell ) => `"${String( cell ?? "" ).replace( /"/g, '""' )}"`
    const csvRows = rows.map( row => [
        row.title, row.recording_metadata?.original_name, row.subject_code?.join( ", " ),
        `Module ${row.module_number}`, row.topics_covered?.join( "; " ),
        formatVideoDuration( row.recording_metadata?.duration_seconds || 0 ), formatDate( row.uploaded_at, { showTime: true } ), row.status
    ] )
    const csvContent = [header, ...csvRows].map( row => row.map( escapeCell ).join( "," ) ).join( "\n" )
    const blob = new Blob( [csvContent], { type: "text/csv;charset=utf-8;" } )
    const url = URL.createObjectURL( blob )
    const link = document.createElement( "a" )
    link.href = url
    link.download = `video-repository-${new Date().toISOString().slice( 0, 10 )}.csv`
    document.body.appendChild( link )
    link.click()
    document.body.removeChild( link )
    URL.revokeObjectURL( url )
}

const subjectCodesRenderer = row => {

    const subjects = row.subjects

    if ( subjects.length === 0 ) return "Not Available"

    const moreSubjectsList = subjects.slice( 1 ).map( ( subject, idx ) => {
        return <Typography key={subject.subject_code} component="span" sx={{ display: "block", fontSize: "0.75rem", fontWeight: 500 }} >{`${idx + 2}. ${subject.subject_name}`}</Typography>

    } )

    return <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
        {subjects.slice( 0, 1 ).map( subject => {
            return (
                <CustomChip
                    color="secondary"
                    variant='semifilled'
                    key={subject.subject_code}
                    value={subject.subject_code}
                    tooltipTitle={subject.subject_name}
                    noWrap
                    textStyles={{ fontSize: "0.75rem" }}
                />
            )
        } )}
        {subjects.length > 1 && <CustomChip
            color="secondary"
            variant='semifilled'
            tooltipTitle={<Box>
                {moreSubjectsList.map( s => s )}
            </Box>}
            value={`+${subjects.length - 1} More`}
            noWrap
            textStyles={{ fontSize: "0.75rem" }}
        />}

    </Box>
}

// ---- Main page ---------------------------------------------------------------

const HybridClassVideosRepository = observer( () => {

    const [recordingsFetchError, setRecordingsFetchError] = useState( null )
    const [fetchingRecordings, setFetchingRecordings] = useState( true )
    const [recordings, setRecordings] = useState( [] )
    const [subjects, setSubjects] = useState( [] )
    const [search, setSearch] = useState( "" )
    const [filters, setFilters] = useState( { academicYear: "all", institute: "all", subject: "all", module: "all", status: "all" } )

    const [addDialogOpen, setAddDialogOpen] = useState( false )
    const [previewRecording, setPreviewRecording] = useState( null )

    const uploadStore = useHybridClassVideoUploadStore()

    const { palette, border, layout } = useTheme()

    const getRecordings = useCallback( async () => {
        try {
            setFetchingRecordings( true )
            const { data: { data } } = await api.onlineMeetings.fetchHybridClassVideos()
            setRecordings( data )
            setRecordingsFetchError( null )
        } catch ( err ) {
            setRecordingsFetchError( { message: err?.response?.data?.message, code: err?.response?.status || 500 } )
        } finally {
            setFetchingRecordings( false )
        }
    }, [] )

    useEffect( () => {
        getRecordings()
    }, [getRecordings] )

    const getSubjects = useCallback( async () => {
        try {
            const { data: { data } } = await api.onlineMeetings.fetchRecordingSubjects()
            setSubjects( data )
        } catch ( err ) {
            toast( err )
        }
    }, [] )

    useEffect( () => {
        getSubjects()
    }, [getSubjects] )

    useEffect( () => {
        if ( !uploadStore.getJobs.length ) return

        setRecordings( prev => {
            const byId = new Map( prev.map( rec => [rec.id, rec] ) )
            const jobIds = new Set( uploadStore.getJobs.map( job => job.id ) )

            uploadStore.getJobs.forEach( job => {
                const existing = byId.get( job.id ) || {}
                const sourceFile = job.files?.find( f => f.role === "source" )
                const topics = job.topics_covered ? ( job.topics_covered || "" )?.split( "," ) : null
                console.log( job )

                byId.set( job.id, {
                    ...existing,
                    id: job.id,
                    title: job.title,
                    topics_covered: topics || existing.topics_covered,
                    subjects: job.subjects || existing.subjects,
                    module_number: job.module_number ?? existing.module_number,
                    uploaded_at: existing.uploaded_at || null,
                    status: job.status === "saved" ? "uploaded" : job.status,
                    recording_metadata: {
                        ...existing.recording_metadata,
                        ...job.recording_metadata,
                    }
                } )
            } )

            // Active/optimistic job rows float to the top; everything else keeps its existing order.
            const jobRows = uploadStore.getJobs.map( job => byId.get( job.id ) )
            const restRows = Array.from( byId.values() ).filter( rec => !jobIds.has( rec.id ) )

            return [...jobRows, ...restRows]
        } )
    }, [uploadStore, uploadStore] )

    useEffect( () => {
        if ( uploadStore.trayMinimized || uploadStore.lastSavedAt ) {
            getRecordings()
            getSubjects()
        }
    }, [uploadStore.trayMinimized, uploadStore.lastSavedAt, getRecordings, getSubjects] )


    // Institutes have no dedicated endpoint either, so their filter options come straight off the recordings.
    const instituteFilterOptions = useMemo( () => {
        const institutes = [...new Set( recordings.map( rec => rec.institute ).filter( Boolean ) )].sort()
        return [{ label: "All institutes", value: "all" }, ...institutes.map( inst => ( { label: inst, value: inst } ) )]
    }, [recordings] )

    const subjectFilterOptions = useMemo( () => {
        const seen = new Set()
        const uniqueSubjects = subjects.filter( subj => !seen.has( subj.subject_id ) && seen.add( subj.subject_id ) )
        return [
            { label: "All subjects", value: "all" },
            ...uniqueSubjects.map( subj => ( { label: `${subj.subject_code} - ${subj.subject_name}`, value: subj.subject_code } ) )
        ]
    }, [subjects] )

    const updateFilter = ( key, value ) => setFilters( prev => ( { ...prev, [key]: value } ) )
    const resetFilters = () => { setFilters( { academicYear: "all", institute: "all", subject: "all", module: "all", status: "all" } ); setSearch( "" ) }

    const filteredRecordings = useMemo( () => {
        const query = search.trim().toLowerCase()
        return recordings.filter( rec => {
            if ( filters.institute !== "all" && rec.institute !== filters.institute ) return false
            if ( filters.subject !== "all" && !rec.subject_codes.includes( filters.subject ) ) return false
            if ( filters.module !== "all" && +rec.module_number !== +filters.module ) return false
            if ( filters.status !== "all" && rec.status !== filters.status ) return false
            if ( query && !`${rec.title} ${rec.topics_covered?.join( " " )} ${rec.subjects?.map( sub => sub.subject_name ).join( " " )} ${rec.recording_metadata?.original_name}`.toLowerCase().includes( query ) ) return false
            return true
        } )
    }, [recordings, search, filters] )

    const mostRecentUpload = useMemo( () => recordings.reduce( ( latest, rec ) => !latest || rec.uploaded_at > latest ? rec.uploaded_at : latest, null ), [recordings] )

    const handleRecordingDeleted = useCallback( ( id ) => {
        setRecordings( prev => prev.filter( rec => rec.id !== id ) )
    }, [] )

    const getModuleNameMap = useCallback( () => {
        const map = {}
        recordings.forEach( rec => {
            if ( !rec.module_number || !rec.module_name ) return
            ( rec.subject_codes || [] ).forEach( code => {
                if ( !map[code] ) map[code] = {}
                map[code][rec.module_number] = rec.module_name
            } )
        } )
        return map
    }, [recordings] )

    const columns = useMemo( () => [
        {
            field: "name", name: "Recording", width: 250, renderValue: row => row.title, sortable: true, filterable: true,
            renderCell: row => (
                <Box display="flex" gap="12px" alignItems="center">
                    <Box width="56px" height="34px" flexShrink={0} borderRadius="6px" bgcolor={palette.contentBg} display="flex" alignItems="center" justifyContent="center">
                        {Icons.default.Movie}
                    </Box>
                    <Box component={Tooltip} placement="right" disableInteractive title={row.title} minWidth={0}>
                        <>
                            <Typography fontSize="13px" sx={{ maxWidth: 200 }} fontWeight="600" className='truncate-at-2' >{row.title}</Typography>
                            <Typography fontSize="11.5px" sx={{ maxWidth: 200 }} color="textSecondary" noWrap>{row.recording_metadata.original_name || `${row.title}.mp4`}</Typography>
                        </>
                    </Box>
                </Box>
            )
        },
        { field: "institute", name: "Institute", renderCell: row => <CustomChip value={row.institute} color="secondary" variant="semifilled" />, filterable: true, renderValue: row => row.institute },
        { field: "subject", name: "Subject", width: 200, renderCell: row => subjectCodesRenderer( row ), sortable: true, filterable: true, renderValue: row => row.institute },
        { field: "module_number", dataType: "number", name: "Module", width: 200, renderValue: row => row.module_number, renderCell: row => <Typography fontSize="13px" className='truncate-at-1' maxWidth={180} component="span">Module - {String( row.module_number )}{row.module_name ? `: ${row.module_name}` : ""}</Typography>, sortable: true, },
        { field: "topic", name: "Topic", maxWidth: "220px", renderCell: row => <Typography fontSize="13px" color="textSecondary" noWrap title={row.topics_covered?.join( ", " )}>{row.topics_covered?.join( ", " )}</Typography>, filterable: true, renderValue: row => row.topics_covered?.join( "," ) },
        { field: "durationSeconds", name: "Duration", renderCell: row => <Typography fontSize="13px" color="textSecondary">{formatVideoDuration( row.recording_metadata.duration || row.title?.length * 25 )}</Typography> },
        { field: "fileSize", width: 120, name: "Size", renderCell: row => <Typography fontSize="13px" color="textSecondary">{getFileSizeFromBytes( row.recording_metadata.size || 0 )}</Typography> },
        {
            field: "uploaded_at", width: 220, name: "Uploaded",
            renderCell: row => (
                <Typography fontSize="12.5px" color="textSecondary">{formatDate( row.uploaded_at, { format: "lll" } )}</Typography>
            ),
            sortable: true
        },
        {
            field: "status", name: "Status",
            renderCell: row => <CustomChip
                value={row.status}
                color={STATUS_COLOR_MAP[row.status]}
                variant="semifilled"
                startIcon={<Box component="span" sx={{ width: "6px", height: "6px", borderRadius: "50%", bgcolor: `${STATUS_COLOR_MAP[row.status]}.main` }} />}
            />,
            filterable: true
        }
    ], [palette] )


    return (
        <Box padding={layout.pagePadding} overflow="auto" display="flex" flexDirection="column" flexGrow={1} gap={layout.sectionsGap}>

            <Box display="flex" flexWrap="wrap" gap={layout.cardsGap} justifyContent="space-between" alignItems="flex-start">
                <Box>
                    <Typography variant="h1" component="h1" fontWeight="600" fontSize="20px">Hybrid Class Video Repository</Typography>
                    <Typography variant="subtitle2" fontSize="13px" color="textSecondary" marginTop="4px">Upload, tag and manage all class recordings available to students</Typography>
                </Box>
                <Box display="flex" gap={layout.cardsGap} alignItems="center" flexWrap="wrap">
                    <TextField
                        size="small"
                        placeholder="Search by name, topic or subject"
                        value={search}
                        onChange={e => setSearch( e.target.value )}
                        sx={{ minWidth: "260px" }}
                        slotProps={{ input: { startAdornment: <InputAdornment position="start">{Icons.small.SearchIcon}</InputAdornment> } }}
                    />
                    <Button variant="outlined" disableElevation startIcon={Icons.default.AddIcon} sx={{ textTransform: "capitalize" }} onClick={() => setAddDialogOpen( true )}>
                        Add Recording
                    </Button>
                </Box>
            </Box>

            <Box display="flex" flexWrap="wrap" gap={layout.cardsGap} alignItems="flex-end">
                <Box minWidth="150px" display="flex" flexDirection="column">
                    <Typography fontSize="12px" fontWeight="500" color="textSecondary">Institute</Typography>
                    <CustomSelectField disabled={fetchingRecordings} size="small" value={filters.institute} onChange={e => updateFilter( 'institute', e.target.value )} data={instituteFilterOptions} itemValueKey="value" />
                </Box>
                <Box minWidth="200px" display="flex" flexDirection="column">
                    <Typography fontSize="12px" fontWeight="500" color="textSecondary">Subject</Typography>
                    <CustomSelectField disabled={fetchingRecordings} size="small" value={filters.subject} onChange={e => updateFilter( 'subject', e.target.value )} data={subjectFilterOptions} itemValueKey="value" />
                </Box>
                <Box minWidth="150px" display="flex" flexDirection="column">
                    <Typography fontSize="12px" fontWeight="500" color="textSecondary">Module</Typography>
                    <CustomSelectField disabled={fetchingRecordings} size="small" value={filters.module} onChange={e => updateFilter( 'module', e.target.value )} data={moduleFilterOptions} itemValueKey="value" />
                </Box>
                <Box minWidth="150px" display="flex" flexDirection="column">
                    <Typography fontSize="12px" fontWeight="500" color="textSecondary">Status</Typography>
                    <CustomSelectField disabled={fetchingRecordings} size="small" value={filters.status} onChange={e => updateFilter( 'status', e.target.value )} menuTextStyles={menuTextStyles} data={statusFilterOptions} itemValueKey="value" />
                </Box>
                <Button onClick={resetFilters} size="small" color="secondary" startIcon={Icons.default.Replay} sx={{ textTransform: "capitalize", height: "40px" }}>Reset filters</Button>
            </Box>

            {!fetchingRecordings && !recordingsFetchError && <Box border={border[1]} borderRadius={layout.borderRadius} >
                <Box display="flex" flexWrap="wrap" alignItems="center" gap={layout.cardsGap} padding="12px 16px" bgcolor={palette.contentBg} borderBottom={border[1]}>
                    <Typography fontSize="13px" fontWeight="600">{filteredRecordings.length} recording{filteredRecordings.length === 1 ? "" : "s"}</Typography>
                    {mostRecentUpload && <Typography fontSize="12px" color="textSecondary">· last upload {getTimePassedFrom( mostRecentUpload )}</Typography>}
                    <Box marginLeft="auto">
                        <Button size="small" variant="outlined" color="greyed" startIcon={Icons.default.DownloadIcon} onClick={() => exportRecordingsToCsv( filteredRecordings )} sx={{ textTransform: "capitalize" }}>
                            Export CSV
                        </Button>
                    </Box>
                </Box>
                <CustomTable onRowClick={( row ) => setPreviewRecording( row )} containerStyles={{ border: "none", borderRadius: 0, flexGrow: 1, maxHeight: 500 }} dataIdKey="id" columns={columns} data={filteredRecordings} pageSize={50} />
            </Box>}
            {fetchingRecordings && !recordingsFetchError && loadingSkeleton}
            {recordingsFetchError && <Message
                title={recordingsFetchError.message}
                imgCode={recordingsFetchError.code}
                type="error"
                color="error"
                retryFunc={getRecordings}
            />}


            {addDialogOpen && <AddRecordingDialog onClose={() => setAddDialogOpen( false )} availableSubjects={subjects} modules={moduleFilterOptions.filter( mod => mod.value !== "all" )} getModuleNameMap={getModuleNameMap} />}

            {previewRecording && (
                <RecordingPreviewDialog
                    recording={previewRecording}
                    onClose={() => setPreviewRecording( null )}
                    onDeleted={handleRecordingDeleted}
                    onUpdated={getRecordings}
                />
            )}
        </Box>
    )
} )

export default HybridClassVideosRepository